import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const AppProvider = lazy(() => import("@/context/AppContext").then(m => ({ default: m.AppProvider })));
const AppLayout = lazy(() => import("@/components/AppLayout"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const RecipesPage = lazy(() => import("@/pages/RecipesPage"));
const WeeklyPlannerPage = lazy(() => import("@/pages/WeeklyPlannerPage"));
const ShoppingListPage = lazy(() => import("@/pages/ShoppingListPage"));
const PrepPlanPage = lazy(() => import("@/pages/PrepPlanPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const InstallPage = lazy(() => import("./pages/InstallPage"));

const queryClient = new QueryClient();

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const ProtectedRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/auth" replace />;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AppProvider>
        <AppLayout>
          <Routes>
            <Route path="/" element={<RecipesPage />} />
            <Route path="/planner" element={<WeeklyPlannerPage />} />
            <Route path="/shopping" element={<ShoppingListPage />} />
            <Route path="/prep" element={<PrepPlanPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
      </AppProvider>
    </Suspense>
  );
};

const AuthRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <Suspense fallback={<LoadingSpinner />}><AuthPage /></Suspense>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <main>
            <Routes>
              <Route path="/auth" element={<AuthRoute />} />
              <Route path="/install" element={<InstallPage />} />
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </main>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
